import { HttpStatusCode } from "axios";
import { inject, injectable } from "inversify";
import { DateTime } from "luxon";
import { action, type IReactionDisposer, makeObservable, observable, reaction } from "mobx";
import AuthService, { AuthenticationStatus } from "@/src/application/services/auth-service";
import UserService from "@/src/application/services/user-service";
import { UserApi } from "@generated/n3-backend-api";
import BusinessErrorEnum from "@/src/domain/enums/business-error-enum";
import { BusinessException } from "@/src/domain/common/business-exception";
@injectable()
export default class UserServiceImpl extends UserService {
  constructor(
    @inject(UserApi) private readonly userApi: UserApi,
    @inject(AuthService) private readonly authService: AuthService
  ) {
    super();
    makeObservable(this);
    this.unsub = reaction(
      () => this.authService.status,
      async (status) => {
        if (status == AuthenticationStatus.RequestUserInfo) {
          this.authService.setStatus(AuthenticationStatus.FetchingUserInfo);

          const isSuccess = await this.loadUserInfo();

          if (isSuccess) {
            this.authService.setStatus(AuthenticationStatus.Authenticated);
            return;
          }

          this.authService.setStatus(AuthenticationStatus.NotAuthenticated);
        }
      },
      {}
    );
  }

  private readonly unsub: IReactionDisposer;

  dispose(): void {
    this.unsub();
    super.dispose();
  }

  @observable
  public userId: number | null = null;
  @observable
  public email: string | null = null;
  @observable
  public isLoaded: boolean = false;

  @observable
  public deleteAccountTime: DateTime | null = null;

  @action.bound
  public async changePassword(oldPassword: string, newPassword: string) {
    if (this.userId == null) {
      throw new BusinessException(BusinessErrorEnum.UserInfoLoadFailed, "User ID is not loaded");
    }
    try {
      await this.userApi.updatePassword({
        newPassword: newPassword,
        oldPassword: oldPassword,
        userId: this.userId,
      });
    } catch (e) {
      throw new BusinessException(BusinessErrorEnum.PasswordUpdateFailed, e);
    }
  }

  @action
  async scheduleAccountDeletion() {
    if (this.deleteAccountTime) {
      throw new BusinessException(BusinessErrorEnum.AccountDeletionAlreadyScheduled);
    }

    if (this.userId == null) {
      throw new BusinessException(BusinessErrorEnum.UserInfoLoadFailed, "User ID is not loaded");
    }

    const result = await this.userApi.requestDeleteAccount();

    const deleteDate = result.data.deleteDate!;

    this.deleteAccountTime = DateTime.fromISO(deleteDate).startOf("minute");
  }

  @action
  async immediateAccountDeletion() {
    if (this.userId == null) {
      throw new BusinessException(BusinessErrorEnum.UserInfoLoadFailed, "User ID is not loaded");
    }

    const response = await this.userApi.deleteAccount();

    if (response.status !== HttpStatusCode.Ok) {
      throw new BusinessException(
        BusinessErrorEnum.AccountDeletionAlreadyScheduled,
        "Failed to delete account immediately"
      );
    }

    await new Promise((resolve) => setTimeout(resolve, 2000));

    this.userId = null;
    this.email = null;
    this.isLoaded = false;
    this.deleteAccountTime = null;

    await this.authService.logout();
  }

  @action
  async cancelAccountDeletion() {
    if (this.userId == null) {
      throw new BusinessException(BusinessErrorEnum.UserInfoLoadFailed, "User ID is not loaded");
    }

    const response = await this.userApi.cancelDeleteAccount();

    if (response.status !== HttpStatusCode.Ok) {
      throw new BusinessException(
        BusinessErrorEnum.AccountDeletionAlreadyScheduled,
        "Failed to cancel account deletion"
      );
    }

    this.deleteAccountTime = null;
  }

  @action
  async loadUserInfo(): Promise<boolean> {
    try {
      const response = await this.userApi.userInfo();

      if (response.status != HttpStatusCode.Ok) {
        this.isLoaded = false;
        return false;
      }

      const data = response.data;

      this.userId = data.id ?? null;
      this.email = data.email ?? null;
      this.isLoaded = true;
      this.deleteAccountTime =
        data.deleteAccountTime != null
          ? DateTime.fromISO(data.deleteAccountTime).startOf("minute")
          : null;

      return true;
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (e) {
      return false;
    }
  }
}
